import { Box } from '@wix/design-system';
import {
  DEFAULT_PURCHASE_CYCLE,
  getStudioStyling,
  ProductHat,
  STUDIO_RECOMMENDED_BADGE_COLOR,
  useAnonymous,
  useIsSeasonalSale,
  useIsStudioNewUi,
  useProductHat,
} from '@wix/package-picker-shared-studio';
import {
  FeaturesGrid as PlatformFeaturesGrid,
  Price,
} from '@wix/premium-purchase-platform/@components';
import classNames from 'classnames';
import React from 'react';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';
import { SelectButton } from '../SelectButton';
import { StudioPrice } from '../StudioPrice';
import s from './FeaturesGrid.scss';

const RECOMMENDED_COLUMN_WIDTH = 'var(--recommended-column-width)';

interface FeaturesGridProductHeaderProps {
  productId: string;
  isRecommended: boolean;
}

export const FeaturesGridProductHeader = ({
  productId,
  isRecommended,
}: FeaturesGridProductHeaderProps) => {
  const { isAnonymous } = useAnonymous();
  const { data: product, isLoading } =
    StudioPackagePickerModule.SDK.studioOfferingApiClient.hooks.useFetchProduct(
      {
        productId,
      },
    );

  const { isEnterprise, name } = product ?? {};

  const { colors } = getStudioStyling();
  const { hasHat, isRecommendedHat } = useProductHat({ productId });
  const isSeasonalSale = useIsSeasonalSale();
  const isStudioNewUi = useIsStudioNewUi();
  const { t } = StudioPackagePickerModule.internals.useTranslations();

  if (isLoading) {
    return null;
  }

  const showTopBorder = isSeasonalSale ? true : !hasHat;

  const cellProps = isStudioNewUi
    ? {
        border: 'none',
        marginTop: isSeasonalSale ? undefined : '-28px',
        className: classNames({
          [s.recommendedHeader]: isRecommended,
          [s.hatInside]: !isSeasonalSale,
        }),
      }
    : {
        borderTop: showTopBorder ? `3px solid ${colors.borderTop}` : undefined,
      };
  const hatProps = isStudioNewUi
    ? {
        height: '28px',
        borderRadius: '6px',
        ...(isRecommendedHat && { background: STUDIO_RECOMMENDED_BADGE_COLOR }),
      }
    : {};
  const hatBoxProps = isStudioNewUi
    ? {
        width: RECOMMENDED_COLUMN_WIDTH,
        marginLeft: 'auto',
        marginRight: 'auto',
      }
    : {
        marginLeft: isSeasonalSale ? '3px' : undefined,
        marginRight: isSeasonalSale ? '3px' : undefined,
      };

  return (
    <PlatformFeaturesGrid.Header.Cell {...cellProps} gap="SP1">
      <Box
        height="30px"
        marginTop={isSeasonalSale ? '3px' : isStudioNewUi ? undefined : '-28px'}
        {...hatBoxProps}
      >
        {hasHat && <ProductHat productId={productId} {...hatProps} />}
      </Box>
      <Box align="center" marginTop="SP2">
        <PlatformFeaturesGrid.Header.Title
          title={name ?? ''}
          size="small"
          className={isStudioNewUi ? s.productHeaderTitle : undefined}
        />
      </Box>
      <Box
        visibility={isEnterprise ? 'hidden' : 'visible'}
        align={isStudioNewUi ? 'center' : undefined}
      >
        {isStudioNewUi ? (
          <StudioPrice
            productId={productId}
            overrideCycleLabel={`/${t('product_price.month')}`}
            centered
          />
        ) : (
          <Price
            productId={productId}
            cycle={DEFAULT_PURCHASE_CYCLE}
            display={{ hideZeroDecimals: false, inline: false, size: 'tiny' }}
          />
        )}
      </Box>
      {!isAnonymous && (
        <Box
          align="center"
          width="100%"
          marginBottom={isStudioNewUi ? '14px' : 'SP3'}
          marginTop="SP1"
        >
          <Box width={isStudioNewUi ? '128px' : '90%'} align="center">
            <SelectButton
              filled={isRecommended}
              productId={productId}
              source="table"
            />
          </Box>
        </Box>
      )}
    </PlatformFeaturesGrid.Header.Cell>
  );
};
